'use client';
import React from 'react';
import * as Styled from './styled';
import { Stack } from '@/ui/primatives';
import * as Icon from '@/ui/Icon';
import useDataframeContext from '@/components/DataFrame/DataframeContext';
import { IDataFrameField } from '@/lib/dataFrameTypes';

import { EyeOpenIcon, EyeNoneIcon } from '@radix-ui/react-icons';
import { Row } from '@/ui/primatives';

import { UpdateColumn, DeleteColumn, RestoreColumn } from '../../ColumnOperations';

const DataTypeIndicator: React.FC<{ baseDataType?: string; headDataType?: string }> = ({
  baseDataType,
  headDataType,
}) => {
  const isChanged = baseDataType && headDataType && baseDataType !== headDataType;
  const added = headDataType && !baseDataType;
  const removed = baseDataType && !headDataType;
  const className = isChanged ? 'modified' : added ? 'added' : removed ? 'removed' : undefined;
  return (
    <Styled.DataTypeIndicator>
      <Row gap={2} alignItems="center" justifyContent="flex-end">
        {isChanged ? (
          <>
            <span className="removed">({baseDataType})</span>
            <Icon.ArrowRight width={14} />
            <span className="added">({headDataType})</span>
          </>
        ) : (
          <span className={className}>({baseDataType || headDataType})</span>
        )}
      </Row>
    </Styled.DataTypeIndicator>
  );
};

const EditActions: React.FC<{
  field: IDataFrameField;
}> = ({ field }) => {
  const fieldStatus = field.changes?.status;
  if (['deleted', 'modified'].includes(fieldStatus as string)) {
    return (
      <Styled.EditActions>
        <RestoreColumn field={field} />
      </Styled.EditActions>
    );
  }
  if (fieldStatus === 'added') {
    return (
      <Styled.EditActions>
        <DeleteColumn field={field} />
      </Styled.EditActions>
    );
  }
  return (
    <Styled.EditActions>
      <UpdateColumn field={field} />
      <DeleteColumn field={field} />
    </Styled.EditActions>
  );
};

const FieldView: React.FC<{
  field: IDataFrameField;
  viewFieldNames: string[];
  addedFieldNames: string[];
  onToggle: (fieldName: string) => void;
}> = ({ field, viewFieldNames, addedFieldNames, onToggle }) => {
  const { editable } = useDataframeContext();
  // TODO: Where are the base and head fields coming from - is this from an old deprecated spec?
  // const baseField = field.base || field;
  // const headField = field.head || field.changes?.previous;
  const isVisible = viewFieldNames.includes(field.name);
  const isAdded = addedFieldNames.includes(field.name);
  const fieldStatus = field.changes?.status;
  const changeSet = field.changes?.previous;
  const isUpdated = !!changeSet && fieldStatus !== 'deleted';
  return (
    <Styled.SchemaField
      key={field.name}
      $visible={isVisible}
      $isAdded={isAdded}
      $status={fieldStatus}
      $editable={editable}
    >
      <Styled.ColumnVisibility onClick={() => onToggle(field.name)}>
        {isVisible ? <EyeOpenIcon /> : <EyeNoneIcon />}
      </Styled.ColumnVisibility>

      <Stack gap={5}>
        <Styled.SchemaFieldDetails className={`${!!changeSet ? 'current' : ''} ${fieldStatus}`}>
          <div className={`field-name `}>{field.name}</div>
          <DataTypeIndicator baseDataType={field.dtype} headDataType={field.dtype} />
        </Styled.SchemaFieldDetails>
        {isUpdated && (
          <Styled.SchemaFieldDetails className="previous">
            <div className={`field-name`}>{changeSet.name}</div>
            {changeSet.dtype !== field.dtype && (
              <DataTypeIndicator headDataType={changeSet.dtype} baseDataType={changeSet.dtype} />
            )}
          </Styled.SchemaFieldDetails>
        )}
      </Stack>

      {editable && <EditActions field={field} />}
    </Styled.SchemaField>
  );
};

export default FieldView;
